"use client";

import { useEffect, useId, useRef, useState } from "react";

const CONTACT_EMAIL = "info@mccoutureunisex.com";

type State = "idle" | "sending" | "done" | "error" | "unavailable";

// Same shape the server enforces; this only exists to give an inline message
// before a round trip.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const FOCUS_RING =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paper";

/**
 * Footer newsletter capture. Replaces a form that had no action and no handler,
 * so Submit reloaded the page and dropped the address on the floor.
 *
 * Three honest outcomes: subscribed, temporarily failed (retry), or not wired
 * up yet (WIX_API_KEY/WIX_SITE_ID unset on the Worker), in which case it hands
 * the shopper a mailto: so the lead is not lost either way.
 *
 * Abuse filtering: a hidden off-screen `company` honeypot and the time since
 * render (`elapsed`) are sent with the signup; /api/subscribe drops scripted posts.
 */
export default function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState<State>("idle");
  const [formError, setFormError] = useState("");
  const renderedAt = useRef(0);
  const [company, setCompany] = useState("");
  const emailId = useId();
  const errorId = useId();

  useEffect(() => {
    renderedAt.current = Date.now();
  }, []);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (state === "sending") return;
    const trimmed = email.trim();
    if (trimmed.length > 254 || !EMAIL_RE.test(trimmed)) {
      setFormError("Enter a valid email");
      return;
    }
    if (!consent) {
      setFormError("Check the box to subscribe");
      return;
    }
    setFormError("");
    setState("sending");
    try {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "content-type": "application/json" },
        // Elapsed time on the visitor's own clock, so a skewed device clock can't
        // make a real signup look like a scripted one.
        body: JSON.stringify({ email: trimmed, consent, company, elapsed: Date.now() - renderedAt.current }),
      });
      if (res.ok) {
        setState("done");
        setEmail("");
        return;
      }
      const body = (await res.json().catch(() => null)) as { reason?: string } | null;
      setState(body?.reason === "not_configured" ? "unavailable" : "error");
    } catch {
      setState("error");
    }
  }

  if (state === "done") {
    return (
      <p className="text-[13px] text-paper/85" role="status">
        You&apos;re on the list. Watch your inbox for the next drop.
      </p>
    );
  }

  return (
    <form className="relative space-y-3" onSubmit={onSubmit} noValidate>
      <label htmlFor={emailId} className="eyebrow block text-paper/70">
        Email address
      </label>
      <input
        id={emailId}
        type="email"
        name="email"
        required
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (formError) setFormError("");
          if (state !== "idle") setState("idle");
        }}
        placeholder="Email"
        aria-invalid={formError === "Enter a valid email" ? true : undefined}
        aria-describedby={formError ? errorId : undefined}
        autoComplete="email"
        className={`w-full border border-paper/30 bg-transparent px-3 py-2 text-[13px] text-paper placeholder:text-paper/65 focus:border-gold ${FOCUS_RING}`}
      />
      {/* Honeypot: off-screen rather than display:none, which bots detect. */}
      <div
        aria-hidden="true"
        style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}
      >
        <label>
          Company
          <input
            type="text"
            name="company"
            tabIndex={-1}
            autoComplete="off"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
        </label>
      </div>
      <label className="flex items-start gap-2 text-[11px] text-paper/70">
        <input
          type="checkbox"
          name="consent"
          checked={consent}
          onChange={(e) => {
            setConsent(e.target.checked);
            if (formError) setFormError("");
          }}
          className={`mt-0.5 ${FOCUS_RING}`}
        />
        Yes, subscribe me to your newsletter.
      </label>
      <button
        type="submit"
        aria-disabled={!consent || state === "sending"}
        className={`w-full bg-paper px-4 py-2 text-[11px] tracking-[0.18em] uppercase text-ink transition hover:bg-gold aria-disabled:cursor-not-allowed aria-disabled:opacity-50 ${FOCUS_RING}`}
      >
        {state === "sending" ? "Submitting…" : "Submit"}
      </button>

      {formError && (
        <p id={errorId} className="text-[11px] text-paper" role="alert">
          {formError}
        </p>
      )}
      {state === "error" && (
        <p className="text-[11px] text-paper/70" role="status">
          That didn&apos;t go through. Please try again in a moment.
        </p>
      )}
      {state === "unavailable" && (
        <p className="text-[11px] text-paper/70" role="status">
          Signups aren&apos;t live yet —{" "}
          <a href={`mailto:${CONTACT_EMAIL}?subject=Add%20me%20to%20the%20list`} className="underline">
            email us
          </a>{" "}
          and we&apos;ll add you.
        </p>
      )}
    </form>
  );
}
